/**
 * `/2fa-disable/:token` view.
 *
 * Consumes the server-side `POST /auth/2fa/disable-confirm` endpoint with the
 * token landed by the "two-factor disable" recovery email. Mirrors
 * `verify-email-view.ts`: the router renders the node first, then we dispatch
 * the fetch on the next microtask (so a11y users get the "Confirming…"
 * announcement before the result arrives).
 *
 * Outcomes:
 *   - 200 → success panel + link to /sign-in (password-only sign-in now works)
 *   - 4xx → token expired / invalid; offer a path back to sign-in
 */
import { div, h1, main, p, span, text, type Mountable, type Send, type Signal } from '@llui/dom';

import { t } from '../i18n.js';

import { confirmTwofaDisable } from './api.js';

const stateByToken = new Map<string, 'pending' | 'success' | 'error'>();

function runConfirm(token: string, rootSelector: string): void {
  if (stateByToken.get(token) === 'success' || stateByToken.get(token) === 'error') return;
  stateByToken.set(token, 'pending');
  void (async (): Promise<void> => {
    const result = await confirmTwofaDisable(token);
    stateByToken.set(token, result.ok ? 'success' : 'error');
    // Replace the DOM in-place — the route hasn't changed, so we re-paint by
    // mutating the existing card content (avoids hooking the TEA loop for a
    // one-shot async result). Same approach as verify-email-view.
    const cardEl = document.querySelector<HTMLElement>(rootSelector);
    if (!cardEl) return;
    cardEl.innerHTML = '';
    cardEl.classList.add('auth-card--result');

    const head = document.createElement('div');
    head.className = 'auth-card-head';
    const glyph = document.createElement('span');
    glyph.className = `auth-result-glyph auth-result-glyph--${result.ok ? 'success' : 'error'}`;
    glyph.setAttribute('aria-hidden', 'true');
    glyph.textContent = result.ok ? '✓' : '!';
    head.appendChild(glyph);
    const heading = document.createElement('h1');
    heading.textContent = t('auth.twofa_disable.heading');
    head.appendChild(heading);
    cardEl.appendChild(head);

    const body = document.createElement('p');
    body.className = 'auth-card-subtitle';
    body.setAttribute('data-testid', result.ok ? 'twofa-disable-success' : 'twofa-disable-error');
    body.textContent = result.ok ? t('auth.twofa_disable.success') : t('auth.twofa_disable.error');
    cardEl.appendChild(body);

    const linkRow = document.createElement('div');
    linkRow.className = 'auth-form-links auth-form-links--center';
    const link = document.createElement('a');
    // Either way the next step is the sign-in page.
    link.href = '/sign-in';
    link.setAttribute(
      'data-testid',
      result.ok ? 'twofa-disable-continue' : 'twofa-disable-back',
    );
    link.textContent = result.ok
      ? t('auth.twofa_disable.continue_link')
      : t('auth.twofa_disable.request_new_link');
    linkRow.appendChild(link);
    cardEl.appendChild(linkRow);
  })();
}

export function viewTwofaDisable<Model, Msg>(
  _state: Signal<Model>,
  _send: Send<Msg>,
  token: string,
): Mountable {
  const rootSelector = `[data-twofa-disable-token="${CSS.escape(token)}"]`;
  if (typeof window !== 'undefined') {
    queueMicrotask(() => runConfirm(token, rootSelector));
  }
  return main({ role: 'main', class: 'auth-shell', 'data-testid': 'twofa-disable-view' }, [
    div(
      {
        class: 'dl-card dl-card--default dl-card--p-xl auth-card auth-card--result',
        'data-twofa-disable-token': token,
      },
      [
        div({ class: 'auth-card-head' }, [
          span({ class: 'auth-result-glyph auth-result-glyph--pending', 'aria-hidden': 'true' }, [
            text('…'),
          ]),
          h1([text(t('auth.twofa_disable.heading'))]),
        ]),
        p({ class: 'auth-card-subtitle', 'data-testid': 'twofa-disable-pending', role: 'status', 'aria-live': 'polite' }, [
          text(t('auth.twofa_disable.pending')),
        ]),
      ],
    ),
  ]);
}
